import type { ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT } from '@salescore/client-base'
import { selectAllRecordThreadStates, useAppSelector } from '@salescore/client-redux'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
  ViewQueryRecordNode,
} from '@salescore/core'
import { Empty } from 'antd'
import { Array, Number, pipe } from 'effect'
import React, { type CSSProperties, type ReactElement, type ReactNode, useCallback, useMemo, useState } from 'react'

import { DurationTimerEnd } from '../../components/misc/DurationTimer'
import { ViewDataLoading } from '../../components/ViewLoading'
import {
  buildColumnWidths,
  buildHeaderHeight,
  fixedNodeBlockHeader,
  HEADER_RIGHT_WIDTH_MARGIN,
  type RSheetRecordNode,
  type UnionOfRSheetColumn,
} from '../../domain/column'
import { useViewQueryResult } from '../../hooks/useViewQueryResult'
import { isKpiOrKpiPivotContext, type RSheetContext } from '../../recoil/context'
import { useRecordsImpl } from '../../recoil/records'
import { useLoadingState } from '../../recoil/records/hooks'
import type { SalescoreObjectAbility, ViewAbility } from '../../recoil/view/atoms'
import { useCellRangeDrag } from '../hooks/useCellRangeDrag'
import type { SetMainCursorByRecordIdArguments } from '../hooks/useSetMainCursorByRecordId.ts'
import { useVirtualizedBody } from '../hooks/useVirtualizedBody'
import { useHandleKeyDownMutation } from '../recoil/mutations/useHandleKeyDown'
import { RSheetsStyle } from '../util/RSheetsStyle'
import { RSheetBody } from './body/RSheetBody'
import type { RenderCellGroup } from './body/RSheetRow.tsx'
import { RSheetsCursorBox } from './cursor/RSheetsCursorBox'
import { RSheetsHeaderRowMemo } from './header/RSheetsHeaderRow'
import type { BlockHeaderStyle } from './header/RSheetsNodeBlockHeader.tsx'

//
// RecordNodeを表示するためのSheetコンポーネント。略してRSheet。
// 普通の「シート」は行列（＝二次元配列）を表示するものだが、
// RSheetはRecordNode（＝ツリー）の配列を表示する。
//
export const RSheet = <T extends UnionOfRSheetColumn<RSheetRecordNode>>({
  height,
  columns,
  context,
  fixedFooter,
  footer,
  columnVirtualization,
  compiledConfig,
  renderHeaderCell,
  view,
  blockHeaderStyle,
  visibleRowNumber,
  rowExpandable,
  setMainCursorByRecordId,
  renderCellGroup,
  rowHeight,
  ability,
  query,
  setHasChangeToViewSchema,
}: {
  height: CSSProperties['height']
  columns: T[]
  context: RSheetContext
  fixedFooter: (leftFixedColumns: Array<T>, notFixedColumns: Array<T>) => ReactNode
  footer: (totalWidth: number) => ReactNode
  columnVirtualization: boolean
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  renderHeaderCell: (column: T, columnIndex: number) => ReactElement
  view: ViewFieldsFragment
  blockHeaderStyle: BlockHeaderStyle
  visibleRowNumber: number | undefined
  rowExpandable: (row: ViewQueryRecordNode) => boolean
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  renderCellGroup: RenderCellGroup<T>
  rowHeight: number
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList | undefined
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const rows = useRecordsImpl()
  const sheetRootReference = React.useRef<HTMLDivElement | null>(null)
  const [sheetRootElement, setSheetRootElement] = useState<HTMLDivElement | null>(null)
  /**
   * シートのスクロール領域が付いた・外れたときに、ref と state の両方へ反映する
   * - ref が要るのは、await の後でも最新のスクロール領域を読むため。state だけだと、await の間に作り直されたとき外れた要素を読む
   * - state が要るのは、作り直しで再描画させ、useVirtualizedBody に描画範囲を計算し直させるため。ref の変更では再描画されない
   * - useCallback が要るのは、描画のたびに ref が外れて付け直され、RSheet がもう 1 回実行されるのを防ぐため
   */
  const attachSheetRoot = useCallback((element: HTMLDivElement | null) => {
    sheetRootReference.current = element
    setSheetRootElement(element)
  }, [])
  const handleKeyDown = useHandleKeyDownMutation(columns, context, compiledConfig, ability, query, visibleRowNumber)
  const openViewRecordThreads = useAppSelector((state) => selectAllRecordThreadStates(state, { viewId: view.id }))
  const totalWidth = useMemo(() => Number.sumAll(buildColumnWidths(columns)), [columns])

  const [leftFixedColumns, notFixedColumns] = useMemo(
    () => pipe(columns, Array.splitAt(context.fixedLeftColumnIndex)),
    [columns, context.fixedLeftColumnIndex],
  )

  const { isLoading } = useRSheetLoading({ context, view })

  const { expandOnDrag, setDragging, bookDragExpand } = useCellRangeDrag({
    context,
    rows,
    columns,
    compiledConfig,
    ability,
    visibleRowNumber,
  })

  const {
    renderingRowsTop,
    renderingRowsTopIndex,
    renderingRowsBottomIndex,
    renderingRowsBottom,
    renderingColumnsLeftIndex: renderingColumnsLeftIndexOrg,
    renderingColumnsLeft,
    renderingColumnsRightIndex,
  } = useVirtualizedBody(sheetRootElement, rows, notFixedColumns, compiledConfig, visibleRowNumber)
  const renderingColumnsLeftIndex = columnVirtualization ? renderingColumnsLeftIndexOrg : 0
  const notFixedBodyColumns = useMemo(
    () =>
      columnVirtualization // シートの時のみ列の仮想化を行う
        ? notFixedColumns.slice(renderingColumnsLeftIndex, renderingColumnsRightIndex)
        : notFixedColumns,
    [columnVirtualization, notFixedColumns, renderingColumnsLeftIndex, renderingColumnsRightIndex],
  )
  const virtualizedRows = useMemo(
    () => rows.slice(renderingRowsTopIndex, renderingRowsBottomIndex),
    [rows, renderingRowsTopIndex, renderingRowsBottomIndex],
  )

  const { leftFixedNodeBlockHeader, notFixedNodeBlockHeader, maxDepth } = useMemo(
    () => fixedNodeBlockHeader(columns, leftFixedColumns, notFixedColumns),
    [columns, leftFixedColumns, notFixedColumns],
  )

  const { headerHeight, headerTopOffset } = useMemo(() => buildHeaderHeight(columns, context), [columns, context])
  const leftFixedWidth = useMemo(() => Number.sumAll(leftFixedColumns.map((x) => x.width)), [leftFixedColumns])
  const notFixedBodyWidth = useMemo(() => Number.sumAll(notFixedBodyColumns.map((x) => x.width)), [notFixedBodyColumns])
  const notFixedHeaderWidth = useMemo(() => Number.sumAll(notFixedColumns.map((c) => c.width)), [notFixedColumns])
  const headerCommonStyle: CSSProperties = {
    backgroundColor: '#F3F4F6',
    display: 'flex',
    height: headerHeight,
    position: 'sticky',
    top: 0,
  }
  const leftFixedStyle: CSSProperties = {
    left: 0,
    marginRight: 0,
    width: leftFixedWidth,
    position: 'sticky',
    boxShadow: '2px 0px 4px #ddd',
  }
  const bodyStyle: CSSProperties = {
    paddingBottom: renderingRowsBottom,
    paddingTop: renderingRowsTop,
  }
  const notFixedStyle: CSSProperties = {
    marginRight: HEADER_RIGHT_WIDTH_MARGIN,
  }

  return isLoading ? (
    <ViewDataLoading view={view} />
  ) : (
    // テーブル全体
    <div
      // eslint-disable-next-line better-tailwindcss/no-unknown-classes -- rsheets は main.less にスタイル定義あり
      className="rsheets select-none"
      ref={attachSheetRoot}
      style={{
        outline: 'none',
        width: '100%',
        height,
        overflow: 'scroll',
        position: 'relative',
        // overflow: hidden;
        overscrollBehavior: 'none',
        backgroundColor: CONSTANT.colors.backgroundGray,
      }}
      tabIndex={0}
      onMouseMove={(event) => {
        bookDragExpand(event, sheetRootReference.current)
      }}
      onKeyDown={(event) => {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: useHandleKeyDown 内部で hasOpenCommentPopups を確認する。 tsx にこのような条件が書いてあるのはよくない。
        const hasOpenCommentPopups = Object.keys(openViewRecordThreads).length > 0
        if (hasOpenCommentPopups) {
          return
        }
        handleKeyDown(event)
      }}
    >
      <div
        style={{
          position: 'sticky',
          left: 0,
          zIndex: RSheetsStyle.zIndex.cursorWhenLeftFixed,
        }}
      >
        <RSheetsCursorBox
          asFixed={true}
          rootReference={sheetRootReference}
          context={context}
          columns={columns}
          compiledConfig={compiledConfig}
          view={view}
          setDragging={setDragging}
          ability={ability}
          query={query}
          visibleRowNumber={visibleRowNumber}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      </div>

      {/* テーブル本体 */}
      <div
        style={{
          position: 'relative',
          width: totalWidth,
          display: 'flex',
          flexFlow: 'wrap',
        }}
      >
        {leftFixedColumns.length === 0 ? null : (
          <div
            style={{
              ...headerCommonStyle,
              ...leftFixedStyle,
              zIndex: RSheetsStyle.zIndex.headerRowWhenLeftFixed,
            }}
            data-e2e={`rsheets-header-row-left-fixed`}
          >
            <RSheetsHeaderRowMemo
              targetColumns={leftFixedColumns}
              headerTopOffset={headerTopOffset}
              columnIndexStart={0}
              maxDepth={maxDepth}
              nodeBlockHeader={leftFixedNodeBlockHeader}
              width={leftFixedWidth}
              renderHeaderCell={renderHeaderCell}
              blockHeaderStyle={blockHeaderStyle}
            />
          </div>
        )}
        <div
          style={{
            ...headerCommonStyle,
            ...notFixedStyle,
            width: notFixedHeaderWidth,
            zIndex: RSheetsStyle.zIndex.headerRow,
          }}
          data-e2e={`rsheets-header-row-not-fixed`}
        >
          <RSheetsHeaderRowMemo
            targetColumns={notFixedColumns}
            headerTopOffset={headerTopOffset}
            columnIndexStart={context.fixedLeftColumnIndex}
            maxDepth={maxDepth}
            nodeBlockHeader={notFixedNodeBlockHeader}
            width={notFixedHeaderWidth}
            renderHeaderCell={renderHeaderCell}
            blockHeaderStyle={blockHeaderStyle}
          />
        </div>

        {rows.length === 0 ? (
          <div
            style={{
              width: '100vw',
            }}
            className="bg-white p-8"
          >
            <Empty />
          </div>
        ) : (
          <>
            {leftFixedColumns.length === 0 ? null : (
              <div
                style={{
                  ...bodyStyle,
                  ...leftFixedStyle,
                  marginLeft: 0,
                  zIndex: RSheetsStyle.zIndex.bodyRowWhenLeftFixed,
                }}
              >
                <RSheetBody
                  targetColumns={leftFixedColumns}
                  virtualizedRows={virtualizedRows}
                  renderingRowsTopIndex={renderingRowsTopIndex}
                  columnIndexStart={0}
                  setDragging={setDragging}
                  expandOnDrag={expandOnDrag}
                  renderCellGroup={renderCellGroup}
                  rowExpandable={rowExpandable}
                  visibleRowNumber={visibleRowNumber}
                  setMainCursorByRecordId={setMainCursorByRecordId}
                  rowHeight={rowHeight}
                />
              </div>
            )}
            <div
              style={{
                ...bodyStyle,
                ...notFixedStyle,
                marginLeft: columnVirtualization ? renderingColumnsLeft : 0,
                width: notFixedBodyWidth,
              }}
            >
              <RSheetBody
                targetColumns={notFixedBodyColumns}
                virtualizedRows={virtualizedRows}
                renderingRowsTopIndex={renderingRowsTopIndex}
                columnIndexStart={context.fixedLeftColumnIndex + renderingColumnsLeftIndex}
                setDragging={setDragging}
                expandOnDrag={expandOnDrag}
                renderCellGroup={renderCellGroup}
                rowExpandable={rowExpandable}
                visibleRowNumber={visibleRowNumber}
                setMainCursorByRecordId={setMainCursorByRecordId}
                rowHeight={rowHeight}
              />
            </div>
          </>
        )}

        {fixedFooter(leftFixedColumns, notFixedColumns)}
        {footer(totalWidth)}

        <RSheetsCursorBox
          asFixed={false}
          rootReference={sheetRootReference}
          context={context}
          columns={columns}
          compiledConfig={compiledConfig}
          view={view}
          setDragging={setDragging}
          ability={ability}
          query={query}
          visibleRowNumber={visibleRowNumber}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      </div>
      <DurationTimerEnd view={view} />
    </div>
  )
}

const useRSheetLoading = ({
  context,
  view,
}: {
  context: RSheetContext
  view: ViewFieldsFragment
}): { isLoading: boolean } => {
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: ローディング周りのロジックが煩雑になってしまっているので改善
  // loadingは別で表示するので、ここのロジックにいれないこと
  const loadingState = useLoadingState()
  const viewQueryResult = useViewQueryResult(view)
  // sheetの場合は、新規読み込みの時のみローディング表示を行う(=cursorもresultもないとき)
  const loadingForSheet = loadingState.isTrue && viewQueryResult?.nextCursor === undefined
  // eslint-disable-next-line sonarjs/todo-tag
  const loadingForKpi = loadingState.isTrue || viewQueryResult === undefined // TODO: 現在のロジックだと集計軸を変えた際にチラつきが発生しやすい。。parameterを使わないようにする？
  const isLoading = isKpiOrKpiPivotContext(context) ? loadingForKpi : loadingForSheet

  return { isLoading }
}
